const defaultInit = () => "1"

// Based on https://github.com/zeit/async-sema

/**
 * `nr` The maximum number of callers allowed to acquire the semaphore concurrently.
 * `initFn` Function that is used to initialize the tokens used to manage the semaphore.
 */
export default class Semaphore {
    constructor(nr, { initFn = defaultInit } = {}) {
        this.nrTokens = nr
        this.free = []
        this.waiting = []
        this.noTokens = initFn == defaultInit

        for (let i = 0; i < nr; i++) {
            this.free.push(initFn())
        }
    }

    /**
     * Acquire a token from the semaphore, thus decrement the number of available execution slots.
     * If `initFn` is not used then the return value of the function can be discarded.
     */
    acquire() {
        const token = this.free.pop()
        if (token) return Promise.resolve(token)

        return new Promise((resolve, reject) => {
            this.waiting.push({ resolve, reject })
        })
    }

    /**
     * Release the semaphore, thus increment the number of free execution slots.
     * If `initFn` is used then the token returned by `acquire()` should be given as an
     * argument when calling this function.
     */
    release(token) {
        if (this.noTokens) token = "1"
        const p = this.waiting.shift()
        if (p) {
            p.resolve(token)
        } else {
            this.free.push(token)
        }
    }

    /**
     * Drains the semaphore and returns all the initialized tokens in an array.
     * Draining is an ideal way to ensure there are no pending async tasks, for example before a process will terminate.
     */
    drain() {
        const a = new Array(this.nrTokens)
        for (let i = 0; i < this.nrTokens; i++) {
            a[i] = this.acquire()
        }
        return Promise.all(a)
    }
}

export function RateLimiter(
    rps,
    { timeUnit = 1000, equalDistribution = false } = {}
) {
    const sema = new Semaphore(equalDistribution ? 1 : rps)
    const delay = equalDistribution ? timeUnit / rps : timeUnit

    return async function rl() {
        await sema.acquire()
        setTimeout(() => sema.release(), delay)
    }
}
